import React, { useState, useEffect, useRef } from "react";
import { InputText } from "primereact/inputtext";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { OverlayPanel } from "primereact/overlaypanel";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { ProgressSpinner } from "primereact/progressspinner";
import { useNavigate, useLocation } from "react-router-dom";
import { useSearch } from "../contexts/SearchContext";
import { useGlobalSearch } from "../contexts/GlobalSearchContext";

interface GlobalSearchBarProps {
  className?: string;
  style?: React.CSSProperties;
}

export const GlobalSearchBar: React.FC<GlobalSearchBarProps> = ({
  className,
  style,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { searchTerm: localSearchTerm, setSearchTerm: setLocalSearchTerm } =
    useSearch();
  const {
    setGlobalSearchTerm,
    globalSearchResults,
    isGlobalSearchLoading,
    globalSearchError,
    performGlobalSearch,
    clearGlobalSearch,
  } = useGlobalSearch();

  const [inputValue, setInputValue] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const overlayRef = useRef<OverlayPanel>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Check if current page supports local search
  const isSchedulePage =
    location.pathname.toLowerCase().replace(/\/$/, "") === "/schedule" ||
    location.pathname.toLowerCase().startsWith("/schedule/");

  const isAdminPage =
    location.pathname.toLowerCase().replace(/\/$/, "") === "/admin" ||
    location.pathname.toLowerCase().startsWith("/admin/");

  const supportsLocalSearch = isSchedulePage || isAdminPage;

  // Sync input with local search term when on supported page
  useEffect(() => {
    if (supportsLocalSearch && localSearchTerm !== inputValue) {
      setInputValue(localSearchTerm);
    }
  }, [localSearchTerm, supportsLocalSearch, inputValue]);

  // Clear search when navigating away from pages that support it or between different supported pages
  useEffect(() => {
    if (!supportsLocalSearch && localSearchTerm) {
      setLocalSearchTerm("");
      setInputValue("");
      clearGlobalSearch();
    }
  }, [
    location.pathname,
    supportsLocalSearch,
    localSearchTerm,
    setLocalSearchTerm,
    clearGlobalSearch,
  ]);

  // Reset search on any route change for cleaner UX
  useEffect(() => {
    setInputValue("");
    setLocalSearchTerm("");
    clearGlobalSearch();
    setShowDropdown(false);
    if (overlayRef.current) {
      overlayRef.current.hide();
    }
  }, [location.pathname, setLocalSearchTerm, clearGlobalSearch]);

  // Handle input changes with debounced global search
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);

    // Update local search immediately if on supported page
    if (supportsLocalSearch) {
      setLocalSearchTerm(value);
    }

    // Update global search term
    setGlobalSearchTerm(value);

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce global search (300ms delay)
    if (value.trim()) {
      searchTimeoutRef.current = setTimeout(() => {
        performGlobalSearch(value);
        if (!showDropdown) {
          setShowDropdown(true);
          overlayRef.current?.show(undefined, inputRef.current);
        }
      }, 300);
    } else {
      setShowDropdown(false);
      overlayRef.current?.hide();
      clearGlobalSearch();
    }
  };

  // Handle focus to show dropdown if there are results
  const handleFocus = () => {
    if (
      globalSearchResults.length > 0 ||
      globalSearchError ||
      isGlobalSearchLoading ||
      inputValue.trim()
    ) {
      setShowDropdown(true);
      overlayRef.current?.show(undefined, inputRef.current);
    }
  };

  // Handle project click
  const handleProjectClick = (project: any) => {
    navigate(`/project/${project.reference}`);
    setShowDropdown(false);
    overlayRef.current?.hide();
    setInputValue("");
    setLocalSearchTerm("");
    clearGlobalSearch();
  };

  // Handle local filter option click
  const handleLocalFilterClick = () => {
    setShowDropdown(false);
    overlayRef.current?.hide();
    // Focus is already on the input and local search is already active
    inputRef.current?.focus();
  };

  // Handle escape key to close dropdown and enter key for local search
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setShowDropdown(false);
      overlayRef.current?.hide();
    } else if (e.key === "Enter" && supportsLocalSearch && inputValue.trim()) {
      // On Enter, focus on local search and close overlay
      setShowDropdown(false);
      overlayRef.current?.hide();
      // Local search is already active via handleInputChange, just close the overlay
      inputRef.current?.blur(); // Optional: remove focus to indicate action completed
    }
  };

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className={className} style={style}>
      <IconField iconPosition="left">
        <InputIcon className="pi pi-search" />
        <InputText
          ref={inputRef}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={
            supportsLocalSearch
              ? "Search this page & global search"
              : "Global search"
          }
          className="p-inputtext-sm"
          style={{ width: "100%" }}
        />
      </IconField>

      <OverlayPanel
        ref={overlayRef}
        showCloseIcon={false}
        dismissable={true}
        onHide={() => setShowDropdown(false)}
        style={{ width: "500px", maxHeight: "300px", overflow: "auto" }}
        className="p-overlaypdanel-flipped"
      >
        {showDropdown && (
          <div className="p-1">
            {/* Local search option (only on supported pages) */}
            {supportsLocalSearch && inputValue.trim() && (
              <>
                <Button
                  text
                  className="p-button-sm w-full justify-content-start p-1 mb-1"
                  onClick={handleLocalFilterClick}
                >
                  <i className="pi pi-filter mr-2 text-xs"></i>
                  <span className="text-sm">Filter current page</span>
                </Button>
              </>
            )}

            {/* Global search section with header */}
            {inputValue.trim() && (
              <>
                <div className="flex align-items-center my-1">
                  <Divider className="flex-1" />
                  <span className="px-2 text-xs text-500 bg-white">
                    Global search results
                  </span>
                  <Divider className="flex-1" />
                </div>
              </>
            )}

            {/* Global search results */}
            {isGlobalSearchLoading && (
              <div className="flex align-items-center p-2">
                <ProgressSpinner
                  style={{ width: "16px", height: "16px" }}
                  strokeWidth="4"
                />
                <span className="ml-2 text-sm text-600">Searching...</span>
              </div>
            )}

            {globalSearchError && (
              <div className="text-red-500 text-xs p-2 text-left">
                <i className="pi pi-exclamation-triangle mr-1"></i>
                {globalSearchError}
              </div>
            )}

            {!isGlobalSearchLoading &&
              !globalSearchError &&
              globalSearchResults.length === 0 &&
              inputValue.trim() && (
                <div className="text-600 text-xs p-2 text-left">
                  <i className="pi pi-info-circle mr-1"></i>
                  No projects found
                </div>
              )}

            {!isGlobalSearchLoading && globalSearchResults.length > 0 && (
              <div>
                {globalSearchResults
                  .slice() // Create a copy to avoid mutating the original array
                  .sort((a, b) => {
                    // First sort by client name ASC
                    const clientCompare = a.client.name.localeCompare(
                      b.client.name
                    );
                    if (clientCompare !== 0) return clientCompare;

                    // Then sort by project reference DESC within the same client
                    return b.reference.localeCompare(a.reference);
                  })
                  .map((project) => (
                    <Button
                      key={project.id}
                      text
                      className="p-button-sm w-full justify-content-start p-1 mb-1"
                      onClick={() => handleProjectClick(project)}
                    >
                      <div className="w-full text-left">
                        <div className="text-sm font-medium text-left">
                          {project.name}
                        </div>
                        <div className="text-xs text-500 text-left">
                          {project.client.name} • {project.reference}
                        </div>
                      </div>
                    </Button>
                  ))}
              </div>
            )}

            {!inputValue.trim() && !isGlobalSearchLoading && (
              <div className="text-600 text-xs p-2 text-left">
                <i className="pi pi-search mr-1"></i>
                Type to search projects
              </div>
            )}
          </div>
        )}
      </OverlayPanel>
    </div>
  );
};
